import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import './FollowUpTracker.css';

function FollowUpTracker() {
  const [followUps, setFollowUps] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    employee: 'all',
    status: 'all',
    dateRange: 'all',
    completed: 'all'
  });
  const [employees, setEmployees] = useState([]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      // Filter sales employees
      const salesEmployees = response.data.filter(emp => 
        emp.role?.name === 'Sales' || emp.role?.permissions?.some(p => p.module === 'leads')
      );
      setEmployees(salesEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchFollowUps = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          params.append(key, filters[key]);
        }
      });

      const response = await api.get(`/lead-assignments/follow-ups/tracker?${params.toString()}`);
      setFollowUps(response.data.followUps || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchFollowUps();
  }, [fetchFollowUps]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDateStatus = (dateString, completed) => {
    if (completed) return 'completed';
    if (!dateString) return 'no-date';
    
    const followUpDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    followUpDate.setHours(0, 0, 0, 0);
    
    if (followUpDate < today) return 'overdue';
    if (followUpDate.getTime() === today.getTime()) return 'today';
    return 'upcoming';
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      new: '#007bff',
      contacted: '#17a2b8',
      interested: '#28a745',
      negotiating: '#ffc107',
      won: '#20c997',
      lost: '#dc3545'
    };
    return statusColors[status] || '#6c757d';
  };

  if (loading) {
    return (
      <div className="follow-up-tracker loading">
        <div className="loading-spinner">
          <p>Loading follow-up data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="follow-up-tracker">
      <div className="tracker-header">
        <h2>📅 Sales Follow-up Tracker</h2>
        <p className="tracker-subtitle">Monitor all sales team follow-ups and activities</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-number">{stats.total || 0}</div>
          <div className="stat-label">Total Follow-ups</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-number">{stats.pending || 0}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card today">
          <div className="stat-number">{stats.today || 0}</div>
          <div className="stat-label">Due Today</div>
        </div>
        <div className="stat-card overdue">
          <div className="stat-number">{stats.overdue || 0}</div>
          <div className="stat-label">Overdue</div>
        </div>
        <div className="stat-card completed">
          <div className="stat-number">{stats.completed || 0}</div>
          <div className="stat-label">Completed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Sales Person:</label>
            <select 
              value={filters.employee} 
              onChange={(e) => handleFilterChange('employee', e.target.value)}
            >
              <option value="all">All Sales People</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>
                  {emp.firstName} {emp.lastName} ({emp.employeeId})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Lead Status:</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="interested">Interested</option>
              <option value="negotiating">Negotiating</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Date Range:</label>
            <select 
              value={filters.dateRange} 
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="upcoming">Upcoming</option>
              <option value="overdue">Overdue</option>
              <option value="this_week">This Week</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Completion:</label>
            <select 
              value={filters.completed} 
              onChange={(e) => handleFilterChange('completed', e.target.value)}
            >
              <option value="all">All</option>
              <option value="false">Pending</option>
              <option value="true">Completed</option>
            </select>
          </div>
        </div>

        <button 
          className="clear-filters-btn"
          onClick={() => setFilters({
            employee: 'all',
            status: 'all', 
            dateRange: 'all',
            completed: 'all'
          })}
        >
          Clear Filters
        </button>
      </div>

      {/* Follow-ups List */}
      <div className="follow-ups-container">
        {followUps.length === 0 ? (
          <div className="no-data">
            <p>No follow-ups found matching the selected criteria.</p>
          </div>
        ) : (
          <div className="follow-ups-grid">
            {followUps.map(followUp => (
              <div 
                key={followUp._id} 
                className={`follow-up-card ${getDateStatus(followUp.nextFollowUp, followUp.followUpCompleted)}`}
              >
                <div className="card-header">
                  <div className="lead-info">
                    <h3 className="lead-name">{followUp.leadName}</h3>
                    <p className="company">{followUp.company || 'No Company'}</p>
                  </div>
                  <div 
                    className="status-badge"
                    style={{ backgroundColor: getStatusBadge(followUp.status) }}
                  >
                    {followUp.status?.toUpperCase()}
                  </div>
                </div>

                <div className="card-body">
                  <div className="follow-up-details">
                    <div className="detail-row">
                      <span className="label">👤 Sales Person:</span>
                      <span className="value">
                        {followUp.employee?.firstName} {followUp.employee?.lastName}
                        <span className="employee-id"> ({followUp.employee?.employeeId})</span>
                      </span>
                    </div>

                    <div className="detail-row">
                      <span className="label">📞 Contact:</span>
                      <span className="value">{followUp.contactNumber}</span>
                    </div>

                    {followUp.email && (
                      <div className="detail-row">
                        <span className="label">✉️ Email:</span>
                        <span className="value">{followUp.email}</span>
                      </div>
                    )}

                    <div className="detail-row">
                      <span className="label">📅 Follow-up Date:</span>
                      <span className="value follow-up-date">
                        {formatDate(followUp.nextFollowUp)} 
                        {followUp.nextFollowUp && ` at ${formatTime(followUp.nextFollowUp)}`}
                        <span className={`date-indicator ${getDateStatus(followUp.nextFollowUp, followUp.followUpCompleted)}`}>
                          {getDateStatus(followUp.nextFollowUp, followUp.followUpCompleted) === 'overdue' && ' (OVERDUE)'}
                          {getDateStatus(followUp.nextFollowUp, followUp.followUpCompleted) === 'today' && ' (TODAY)'}
                          {getDateStatus(followUp.nextFollowUp, followUp.followUpCompleted) === 'completed' && ' (COMPLETED)'}
                        </span>
                      </span>
                    </div>

                    {followUp.value > 0 && (
                      <div className="detail-row">
                        <span className="label">💰 Value:</span>
                        <span className="value">₹{followUp.value?.toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    {followUp.followUpNotes && (
                      <div className="detail-row">
                        <span className="label">📝 Notes:</span>
                        <span className="value notes">{followUp.followUpNotes}</span>
                      </div>
                    )}

                    {followUp.followUpCompleted && (
                      <div className="completion-info">
                        <div className="detail-row">
                          <span className="label">✅ Completed on:</span>
                          <span className="value">{formatDate(followUp.followUpCompletedDate)}</span>
                        </div>
                        {followUp.followUpCompletedNotes && (
                          <div className="detail-row">
                            <span className="label">📋 Completion Notes:</span>
                            <span className="value notes">{followUp.followUpCompletedNotes}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-footer">
                  <small className="assigned-by">
                    Assigned by: {followUp.assignedBy?.firstName} {followUp.assignedBy?.lastName}
                  </small>
                  <small className="created-date">
                    Lead created: {formatDate(followUp.createdAt)}
                  </small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FollowUpTracker;